import { API_CONFIG, ERROR_MESSAGES } from '@/constants'
import { ApiResponse, HttpClient, RequestConfig } from '../types'

class ApiClient implements HttpClient {
  private readonly baseURL: string
  private readonly timeout: number
  private readonly retryAttempts: number
  private tokenGetter: () => string | null
  private unauthorizedCount = 0
  private maxUnauthorizedAttempts = 3

  constructor(tokenGetter: () => string | null = () => localStorage.getItem('token')) {
    this.baseURL = API_CONFIG.BASE_URL
    this.timeout = API_CONFIG.TIMEOUT
    this.retryAttempts = API_CONFIG.RETRY_ATTEMPTS
    this.tokenGetter = tokenGetter
  }

  protected getAuthToken(): string | null {
    return this.tokenGetter()
  }

  private handleUnauthorized(): void {
    this.unauthorizedCount++
    
    // Only logout after multiple failed attempts
    if (this.unauthorizedCount >= this.maxUnauthorizedAttempts) {
      this.logoutUser()
    }
  }

  private logoutUser(): void {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    this.unauthorizedCount = 0
    
    // Redirect to login page
    if (typeof window !== 'undefined') {
      window.location.href = '/login'
    }
  }

  private resetUnauthorizedCount(): void {
    if (this.unauthorizedCount > 0) {
      this.unauthorizedCount = 0
    }
  }

  private getDefaultHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    const token = this.getAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    return headers
  }

  private async makeRequest<T>(config: RequestConfig): Promise<ApiResponse<T>> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    let fullUrl = `${this.baseURL}${config.url}`
    
    // Add query parameters if provided
    if (config.params) {
      const searchParams = new URLSearchParams()
      Object.entries(config.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        fullUrl += `?${queryString}`
      }
    }


    try {
      const response = await fetch(fullUrl, {
        method: config.method,
        headers: {
          ...this.getDefaultHeaders(),
          ...config.headers,
        },
        body: config.data ? JSON.stringify(config.data) : undefined,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (process.env.NODE_ENV === 'development') {
        const errorText = await response.text()
        console.error(`API Error ${response.status}:`, errorText)
        }
        
        // Handle 401 Unauthorized with smart logic
        if (response.status === 401) {
          this.handleUnauthorized()
        }
        
        throw new Error(this.getErrorMessage(response.status))
      }

      // Reset unauthorized count on successful request
      this.resetUnauthorizedCount()
      
      const result = await response.json()
      return { success: true, data: result }
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (process.env.NODE_ENV === 'development') {
      console.error('Request failed:', error)
      }
      if (error instanceof Error) {
        return { 
          success: false, 
          error: error.message === 'The operation was aborted.' 
            ? 'Request timeout' 
            : error.message 
        }
      }

      return { success: false, error: ERROR_MESSAGES.SERVER_ERROR }
    }
  }

  private getErrorMessage(status: number): string {
    switch (status) {
      case 401:
        return ERROR_MESSAGES.UNAUTHORIZED
      case 403:
        return ERROR_MESSAGES.FORBIDDEN
      case 404:
        return ERROR_MESSAGES.NOT_FOUND
      case 422:
        return ERROR_MESSAGES.VALIDATION_ERROR
      case 500:
        return ERROR_MESSAGES.SERVER_ERROR
      default:
        return ERROR_MESSAGES.NETWORK_ERROR
    }
  }

  async request<T>(config: RequestConfig): Promise<ApiResponse<T>> {
    let lastError: ApiResponse<T> | null = null

    for (let attempt = 0; attempt <= this.retryAttempts; attempt++) {
      try {
        const result = await this.makeRequest<T>(config)
        
        if (result.success) {
          return result
        }

        lastError = result
        
        // Don't retry on authentication errors
        if (result.error?.includes('401') || result.error?.includes('403')) {
          break
        }

        // Exponential backoff for retries
        if (attempt < this.retryAttempts) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000))
        }
      } catch (error) {
        lastError = { success: false, error: ERROR_MESSAGES.SERVER_ERROR }
      }
    }

    return lastError || { success: false, error: ERROR_MESSAGES.SERVER_ERROR }
  }

  async get<T>(url: string, config?: { headers?: Record<string, string>, params?: Record<string, any> }): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'GET', url, headers: config?.headers, params: config?.params })
  }

  async post<T>(url: string, data?: unknown, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'POST', url, data, headers })
  }

  async put<T>(url: string, data?: unknown, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'PUT', url, data, headers })
  }

  async delete<T>(url: string, headers?: Record<string, string>): Promise<ApiResponse<T>> {
    return this.request<T>({ method: 'DELETE', url, headers })
  }
}

// Singleton instance
export const apiClient = new ApiClient()

// Service layer abstractions
export abstract class BaseService {
  protected readonly api = apiClient

  protected getAuthToken(): string | null {
    return localStorage.getItem('token')
  }

  protected handleError(error: unknown): never {
    if (error instanceof Error) {
      throw error
    }
    throw new Error(ERROR_MESSAGES.SERVER_ERROR)
  }

  protected async get<T>(endpoint: string, options?: { params?: Record<string, any> }): Promise<T> {
    let url = endpoint
    
    // Add query parameters if provided
    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }
    
    return this.request<T>(url, { method: 'GET' })
  }

  protected async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  protected async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  protected async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getAuthToken()
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    }

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const error = new Error(errorData.message || `HTTP error! status: ${response.status}`) as any
        error.status = response.status
        // Don't log 404 errors as they're often handled gracefully by services
        if (response.status !== 404) {
          console.error('API request failed:', error)
        }
        throw error
      }

      // Check if response body is already consumed
      if (response.bodyUsed) {
        throw new Error('Response body already consumed');
      }
      
      const jsonData = await response.json() as T;
      return jsonData;
    } catch (error: any) {
      // Only log if it's not a 404 (404s are often handled gracefully)
      if (error?.status !== 404) {
        console.error(`[BaseService.request] Error for ${endpoint}:`, error)
      }
      throw error
    }
  }
} 